import "./styles.scss";
import React, { useState, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Dropdown } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { ReactComponent as StopwatchIcon } from "assets/icons/timer-solid-full.svg";
import { ReactComponent as DarkModeIcon } from "assets/icons/moon-solid-full.svg";
import { useAuth } from "features/auth/hooks/useAuth";
import { Stopwatch } from "features/architecture/worksessions/components/Stopwatch";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "core/providers/ThemeContext";
import { useStopwatch } from "core/providers/StopwatchContext";
import { SidebarItem } from "layout/MainLayout/Sidebar/types";
import { sidebarItems } from "../Sidebar";
import { useProjectSelection } from "core/providers/ProjectSelectionContext";
import { RequiredPermissionScope } from "shared/types/AppRoute";
import { useCurrentUser } from "features/collaborator";

export const Header: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const { theme, toggleTheme } = useTheme();

  const { seconds, projectAccumulatedSeconds } = useStopwatch();

  const { projectState } = useProjectSelection();
  const projectLabel = projectState.project?.label ?? "";

  const formatTime = (time: number) => {
    const hrs = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = time % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const {
    isLoading: loading,
    logout,
    hasDomainAccess,
    hasPermission,
    getPermissionScope,
  } = useAuth();
  const currentUser = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  const goProfile = () => navigate("/profile");

  const visibleItems: SidebarItem[] = useMemo(() => {
    return sidebarItems.filter((item) => {
      const isDomainAllowed = !item.domain || hasDomainAccess(item.domain);
      const isVisibleByPermission =
        !item.requiredPermissions?.length ||
        item.requiredPermissions.every((permission) =>
          hasPermission(permission),
        );
      const isVisibleByPermissionAny =
        !item.requiredPermissionsAny?.length ||
        item.requiredPermissionsAny.some((permission) =>
          hasPermission(permission),
        );
      const isVisibleByScope =
        !item.requiredPermissionScopesAny?.length ||
        item.requiredPermissionScopesAny.some(
          ({ permission, scopes }: RequiredPermissionScope) => {
            const scope = getPermissionScope(permission);
            return scope != null && scopes.includes(scope);
          },
        );

      return (
        isDomainAllowed &&
        isVisibleByPermission &&
        isVisibleByPermissionAny &&
        isVisibleByScope
      );
    });
  }, [hasDomainAccess, hasPermission, getPermissionScope]);

  const pageName = useMemo(() => {
    if (!visibleItems.length) return "Dashboard";

    if (
      location.pathname.startsWith("/home/architecture/director/collaborator/")
    ) {
      return "Dashboard Collaborator";
    }

    const exact = visibleItems.find((i) => location.pathname === i.to);
    if (exact) return exact.name;

    const parent = visibleItems
      .filter((i) => location.pathname.startsWith(i.to ?? ""))
      .sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0];

    return parent?.name ?? "Dashboard";
  }, [visibleItems, location.pathname]);

  const userMenuItems = [
    {
      key: "config",
      label: (
        <div onClick={goProfile} className="dropdown-item">
          Config
        </div>
      ),
    },
    {
      key: "logout",
      label: (
        <div onClick={logout} className="dropdown-item">
          Log Out
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <header className="header">
        <div className="header-left">
          <NavLink to="home">SM</NavLink>
          <h2>Loading...</h2>
        </div>
      </header>
    );
  }

  if (!currentUser) {
    return (
      <header className="header">
        <div className="header-left">
          <NavLink to="home">SM</NavLink>
          <h2>Loading...</h2>
        </div>
        <div className="header-right">
          <span className="error-text">You are not logged in...</span>
          <button className="btn-link" onClick={logout}>
            Salir
          </button>
        </div>
      </header>
    );
  }

  return (
    <header className="header">
      <div className="header-left">
        <NavLink to="home">SM</NavLink>
        <h2>{pageName}</h2>
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="stopwatch-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeModal}
          >
            <motion.div
              className="stopwatch-modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="stopwatch-modal-close" onClick={closeModal}>
                &times;
              </button>
              <Stopwatch onStart={closeModal} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="header-right">
        {(projectAccumulatedSeconds > 0 || seconds > 0) && (
          <div className="stopwatch-display-header">
            <span>{formatTime(seconds)}</span>
            <span> - </span>
            <span>{formatTime(projectAccumulatedSeconds + seconds)}</span>
            {projectLabel && (
              <>
                <span> - </span>
                <span>{projectLabel}</span>
              </>
            )}
          </div>
        )}
        <div className="header-modes">
          <button type="button" onClick={openModal} className="icon-container">
            <StopwatchIcon className="icon" fill="#86D921" />
          </button>
          <button
            type="button"
            className="icon-container"
            onClick={toggleTheme}
          >
            <DarkModeIcon
              className="icon"
              fill={theme === "light" ? "#06B6D4" : "#FACC15"}
            />
          </button>
        </div>

        <div className="info-user">
          <img
            className="icon"
            src={`https://i.pravatar.cc/150?u=${currentUser.username}`}
            alt={currentUser.username}
          />

          <div className="info">
            <h1>
              {currentUser.collaborator_profile?.full_name ??
                currentUser.username}
            </h1>
            <h2>{currentUser.collaborator_profile?.position.title ?? ""}</h2>
          </div>

          <Dropdown
            className="dropdown"
            menu={{ items: userMenuItems }}
            trigger={["click"]}
          >
            <DownOutlined />
          </Dropdown>
        </div>
      </div>
    </header>
  );
};
